/* ============================================================================
 * De-para de Produtos XML × Estoque
 *
 * Portado de base-html `erpone_de_para_de_produtos_xml_vs_estoque`:
 *   "Map Invoice Items" — casa a chave do item no XML da nota (GTIN/EAN,
 *   código do fornecedor ou descrição) com o produto do cadastro e o registro
 *   de estoque.
 *
 * Contrato real consumido (fiscal-documents.dto.ts):
 *   GET    /fiscal/product-mapping?xmlKey=&confidence=&page=&limit=
 *            → PaginatedProductMappingDto (data[] ProductMappingResponseDto)
 *   POST   /fiscal/product-mapping       (CreateProductMappingDto)
 *   PUT    /fiscal/product-mapping/:id   (UpdateProductMappingDto)
 *   DELETE /fiscal/product-mapping/:id   (204)
 *   GET    /products?limit=200           (catálogo p/ resolver o nome do produto)
 *
 * O protótipo mostra uma tela de "Confirm Mapping" com pares XML × Local e a
 * conversão de unidades (1 UN XML → 5 UN Local). O backend R2B não expõe
 * endpoint de confirmação em lote nem multiplicador de conversão — o CRUD de
 * mapeamento individual é a ação real. Unidade/conversão foram omitidas.
 *
 * `xmlKey` é imutável no update (UpdateProductMappingDto não o expõe).
 * ========================================================================== */

import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../../shared/auth/AuthContext';
import { formatDate, EMPTY } from '../../../shared/utils/format';
import { Badge, Button, LoadingSkeleton, Modal, useToast } from '@nexora/design-system';
import {
  useProductMappings,
  useCatalogProducts,
  useCreateProductMapping,
  useUpdateProductMapping,
  useDeleteProductMapping,
} from './fiscalDocumentsHooks';
import type {
  ProductMapping,
  CreateProductMappingInput,
  UpdateProductMappingInput,
} from './fiscalDocumentsTypes';
import { FISCAL_DOCUMENTS_CSS } from './fiscalDocumentsCss';

const PAGE_SIZE = 20;

const CONFIDENCE_LABEL: Record<string, string> = {
  AUTO: 'Automático',
  MANUAL: 'Manual',
};

const CONFIDENCE_VARIANT: Record<string, 'success' | 'neutral' | 'info'> = {
  AUTO: 'success',
  MANUAL: 'neutral',
};

/* ── Formulário compartilhado (novo / editar) ── */

interface MappingFormState {
  xmlKey: string;
  xmlDescription: string;
  productId: string;
  estoqueId: string;
  confidence: string;
}

const EMPTY_FORM: MappingFormState = {
  xmlKey: '',
  xmlDescription: '',
  productId: '',
  estoqueId: '',
  confidence: 'MANUAL',
};

interface MappingFormModalProps {
  isOpen: boolean;
  title: string;
  /** Preenchido apenas na edição (xmlKey não é editável). */
  editing?: ProductMapping | null;
  products: Array<{ id: string; name: string; sku: string }>;
  onSubmit: (input: CreateProductMappingInput & UpdateProductMappingInput) => void;
  onCancel: () => void;
  submitting: boolean;
  errorText: string | null;
}

function MappingFormModal({
  isOpen,
  title,
  editing,
  products,
  onSubmit,
  onCancel,
  submitting,
  errorText,
}: MappingFormModalProps): JSX.Element {
  const [form, setForm] = useState<MappingFormState>(EMPTY_FORM);
  const [search, setSearch] = useState('');

  /* Reset ao abrir (troca de id → valores do item em edição). */
  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setForm(
      editing
        ? {
            xmlKey: editing.xmlKey,
            xmlDescription: editing.xmlDescription ?? '',
            productId: editing.productId,
            estoqueId: editing.estoqueId ?? '',
            confidence: editing.confidence,
          }
        : EMPTY_FORM,
    );
  }, [isOpen, editing]);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return products;
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        p.sku.toLowerCase().includes(term) ||
        p.id.toLowerCase().includes(term),
    );
  }, [products, search]);

  const valid =
    form.xmlKey.trim().length > 0 &&
    form.productId.trim().length > 0 &&
    (!!editing || form.xmlKey.trim().length <= 120);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      size="lg"
      footer={
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Button variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!valid || submitting}
            loading={submitting}
            onClick={() =>
              onSubmit({
                xmlKey: form.xmlKey.trim(),
                xmlDescription: form.xmlDescription.trim() || undefined,
                productId: form.productId,
                estoqueId: form.estoqueId.trim() || undefined,
                confidence: form.confidence,
              })
            }
          >
            Salvar
          </Button>
        </div>
      }
    >
      <div className="fiscal-form">
        {errorText && <div className="fiscal-form__error">{errorText}</div>}

        {editing ? (
          <div className="fiscal-form__row">
            <label className="fiscal-form__label">Chave no XML</label>
            <div style={{ padding: '0.5rem', background: '#f2f4f6', border: '1px solid #c4c6cf', borderRadius: '2px', fontSize: '0.875rem' }}>
              {form.xmlKey}
            </div>
            <span className="fiscal-form__hint">
              A chave XML não pode ser alterada — crie um novo de-para se precisar.
            </span>
          </div>
        ) : (
          <div className="fiscal-form__row">
            <label className="fiscal-form__label">
              Chave no XML <span style={{ color: '#ba1a1a' }}>*</span>
            </label>
            <input
              value={form.xmlKey}
              placeholder="GTIN/EAN, código do fornecedor ou descrição normalizada"
              onChange={(e) => setForm((f) => ({ ...f, xmlKey: e.target.value }))}
              aria-label="Chave no XML"
            />
            <span className="fiscal-form__hint">
              É o que identifica o item dentro do XML da nota de entrada. Máx. 120 caracteres.
            </span>
          </div>
        )}

        <div className="fiscal-form__row">
          <label className="fiscal-form__label">Descrição no XML</label>
          <textarea
            value={form.xmlDescription}
            placeholder="Descrição do item como veio no XML (opcional)"
            onChange={(e) => setForm((f) => ({ ...f, xmlDescription: e.target.value }))}
            aria-label="Descrição no XML"
          />
        </div>

        <div className="fiscal-form__row">
          <label className="fiscal-form__label">
            Produto do cadastro <span style={{ color: '#ba1a1a' }}>*</span>
          </label>
          <input
            value={search}
            placeholder="Buscar produto por nome, SKU ou ID..."
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar produto"
          />
          <div style={{ border: '1px solid #c4c6cf', borderRadius: '4px', maxHeight: '180px', overflow: 'auto' }}>
            {filteredProducts.length === 0 && (
              <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.8125rem', color: '#43474e' }}>
                Nenhum produto encontrado.
              </div>
            )}
            {filteredProducts.slice(0, 50).map((p) => {
              const selected = form.productId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, productId: p.id }))}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.5rem 0.75rem',
                    border: 'none',
                    borderBottom: '1px solid #eceef0',
                    background: selected ? '#e8f0f8' : '#ffffff',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                  }}
                >
                  <span style={{ fontWeight: selected ? 700 : 500 }}>{p.name}</span>
                  <span style={{ color: '#43474e', fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11 }}>
                    {' '}· {p.sku}
                  </span>
                </button>
              );
            })}
          </div>
          <span className="fiscal-form__hint">
            {form.productId
              ? `Selecionado: ${products.find((p) => p.id === form.productId)?.name ?? form.productId}`
              : 'Selecione o produto do cadastro que corresponde a esta chave XML.'}
          </span>
        </div>

        <div className="fiscal-form__row">
          <label className="fiscal-form__label">Registro de estoque (ID)</label>
          <input
            value={form.estoqueId}
            placeholder="UUID do StockBalance (opcional)"
            onChange={(e) => setForm((f) => ({ ...f, estoqueId: e.target.value }))}
            aria-label="ID do registro de estoque"
          />
          <span className="fiscal-form__hint">Opcional — é o estoque correspondente, quando conhecido.</span>
        </div>

        <div className="fiscal-form__row">
          <label className="fiscal-form__label">Confiança</label>
          <select
            value={form.confidence}
            onChange={(e) => setForm((f) => ({ ...f, confidence: e.target.value }))}
            aria-label="Confiança do mapeamento"
          >
            <option value="MANUAL">MANUAL — conferido pelo usuário</option>
            <option value="AUTO">AUTO — deduzido automaticamente</option>
          </select>
        </div>
      </div>
    </Modal>
  );
}

/* ── Página ── */

export function ProductMappingPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const { addToast } = useToast();

  const [page, setPage] = useState(0);
  const [confidenceFilter, setConfidenceFilter] = useState('ALL');
  const [searchXml, setSearchXml] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProductMapping | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductMapping | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useProductMappings(tenantId, {
    xmlKey: searchXml || undefined,
    confidence: confidenceFilter,
    page: page + 1,
    limit: PAGE_SIZE,
  });
  const { data: catalog } = useCatalogProducts(tenantId);

  const createMapping = useCreateProductMapping(tenantId);
  const updateMapping = useUpdateProductMapping(tenantId);
  const deleteMapping = useDeleteProductMapping(tenantId);

  const mappings = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const catalogProducts = useMemo(
    () =>
      (catalog ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        sku: p.sku,
      })),
    [catalog],
  );

  const productName = (id: string): string => {
    const p = catalogProducts.find((c) => c.id === id);
    return p ? `${p.name} (${p.sku})` : id;
  };

  const handleSubmit = async (
    input: CreateProductMappingInput & UpdateProductMappingInput,
  ): Promise<void> => {
    setFormError(null);
    try {
      if (editing) {
        await updateMapping.mutateAsync({
          id: editing.id,
          input: {
            xmlDescription: input.xmlDescription,
            productId: input.productId,
            estoqueId: input.estoqueId,
            confidence: input.confidence,
          },
        });
        addToast('De-para atualizado', 'success');
      } else {
        await createMapping.mutateAsync({
          xmlKey: input.xmlKey,
          xmlDescription: input.xmlDescription,
          productId: input.productId,
          estoqueId: input.estoqueId,
          confidence: input.confidence,
        } as CreateProductMappingInput);
        addToast('De-para criado', 'success');
      }
      setModalOpen(false);
      setEditing(null);
    } catch (err) {
      setFormError((err as Error).message ?? 'Erro ao salvar o de-para');
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    try {
      await deleteMapping.mutateAsync(deleteTarget.id);
      addToast('De-para excluído', 'success');
      setDeleteTarget(null);
    } catch (err) {
      addToast((err as Error).message ?? 'Erro ao excluir o de-para', 'error');
    }
  };

  return (
    <div className="fiscal-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            De-para de Produtos XML × Estoque
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Mapeie a chave do item no XML da nota para o produto do cadastro e o estoque.
          </p>
        </div>
        <Button variant="primary" onClick={() => { setEditing(null); setFormError(null); setModalOpen(true); }}>
          Novo de-para
        </Button>
      </div>

      {/* ── Filtros ── */}
      <div className="fiscal-toolbar">
        <input
          className="fiscal-control"
          placeholder="Buscar por chave XML..."
          value={searchXml}
          onChange={(e) => {
            setSearchXml(e.target.value);
            setPage(0);
          }}
          aria-label="Buscar por chave XML"
        />
        <select
          className="fiscal-control fiscal-control--select"
          value={confidenceFilter}
          onChange={(e) => {
            setConfidenceFilter(e.target.value);
            setPage(0);
          }}
          aria-label="Filtrar por confiança"
        >
          <option value="ALL">Todas as confianças</option>
          <option value="AUTO">Automático</option>
          <option value="MANUAL">Manual</option>
        </select>
        <span className="fiscal-count">
          {total} {total === 1 ? 'mapeamento' : 'mapeamentos'}
        </span>
      </div>

      {/* ── Tabela ── */}
      <div className="fiscal-card">
        {isLoading && (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={6} height="40px" />
          </div>
        )}

        {error && !isLoading && (
          <div className="fiscal-state">
            <p className="fiscal-state__title">Erro ao carregar os de-para</p>
            <p className="fiscal-state__detail">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && mappings.length === 0 && (
          <div className="fiscal-state">
            <p className="fiscal-state__detail">
              Nenhum de-para cadastrado. Use “Novo de-para” para mapear a primeira chave XML.
            </p>
          </div>
        )}

        {!isLoading && !error && mappings.length > 0 && (
          <>
            <div className="fiscal-table">
              <table>
                <thead>
                  <tr>
                    <th>Chave XML</th>
                    <th>Descrição no XML</th>
                    <th>Produto (cadastro)</th>
                    <th>Estoque</th>
                    <th>Confiança</th>
                    <th>Atualizado em</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m) => (
                    <tr key={m.id}>
                      <td className="fiscal-mono" style={{ fontWeight: 500 }}>
                        {m.xmlKey}
                      </td>
                      <td>{m.xmlDescription ?? EMPTY}</td>
                      <td style={{ wordBreak: 'break-word' }}>{productName(m.productId)}</td>
                      <td className="fiscal-mono">{m.estoqueId ?? EMPTY}</td>
                      <td>
                        <Badge variant={CONFIDENCE_VARIANT[m.confidence] ?? 'neutral'} size="sm">
                          {CONFIDENCE_LABEL[m.confidence] ?? m.confidence}
                        </Badge>
                      </td>
                      <td className="fiscal-nowrap">{formatDate(m.updatedAt)}</td>
                      <td className="fiscal-actions">
                        <Button variant="ghost" size="sm" onClick={() => { setEditing(m); setFormError(null); setModalOpen(true); }}>
                          Editar
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(m)}>
                          Excluir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="fiscal-footer">
              <span className="font-body-sm text-body-sm text-on-surface-variant tabular-nums">
                {page * PAGE_SIZE + 1}–{Math.min(total, page * PAGE_SIZE + mappings.length)} de{' '}
                {total}
              </span>
              <div className="flex gap-xs">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Novo / Editar ── */}
      <MappingFormModal
        isOpen={modalOpen}
        title={editing ? 'Editar de-para' : 'Novo de-para'}
        editing={editing}
        products={catalogProducts}
        onSubmit={(input) => void handleSubmit(input)}
        onCancel={() => { setModalOpen(false); setEditing(null); setFormError(null); }}
        submitting={createMapping.isPending || updateMapping.isPending}
        errorText={formError}
      />

      {/* ── Confirmar exclusão ── */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Excluir de-para"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleDelete()}
              loading={deleteMapping.isPending}
            >
              Excluir
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, fontSize: '0.875rem', color: '#191c1e' }}>
          Excluir o de-para da chave{' '}
          <strong className="fiscal-mono">{deleteTarget?.xmlKey ?? ''}</strong>? Essa ação não pode
          ser desfeita.
        </p>
      </Modal>

      <style>{FISCAL_DOCUMENTS_CSS}</style>
    </div>
  );
}